import React from "react";
import { useDispatch } from "react-redux";
import { useIframeCommunication } from "@/hooks/useIframeCommunication";
import { setTheme, setCustomColors } from "@/core/app/reducer";
import { ThemeChangeData } from "@/types/iframe-messages";
import { PaletteOptions } from "@mui/material/styles";

interface AppInitializerProps {
  children: React.ReactNode;
}

/**
 * AppInitializer component that handles iframe communication
 * and app initialization logic
 */
export const AppInitializer: React.FC<AppInitializerProps> = ({ children }) => {
  const dispatch = useDispatch();

  // Get trusted origins from environment variables, fallback to "*"
  const getTrustedOrigins = (): string[] => {
    const envOrigins = import.meta.env.VITE_TRUSTED_ORIGINS;
    if (envOrigins) {
      // Split by comma and trim whitespace
      return envOrigins.split(",").map((origin: string) => origin.trim());
    }
    return ["*"]; // Fallback to wildcard if no environment variable
  };

  // Initialize iframe communication with comprehensive options
  const iframeComm = useIframeCommunication({
    autoDetectIframe: true,
    autoSendReady: true,
    enableLogging: process.env.NODE_ENV === "development",
    defaultSource: "delivery",
    capabilities: ["ordering", "theming", "closing", "navigation"],

    onThemeChange: (themeData: ThemeChangeData) => {
      console.log("Theme change received from parent:", themeData);

      // Update Redux state with new theme
      dispatch(setTheme(themeData.theme));

      // If custom colors are provided, store them in Redux
      if (themeData.colors) {
        dispatch(
          setCustomColors(themeData.colors as unknown as PaletteOptions)
        );
      }

      // Log theme application for debugging
      if (process.env.NODE_ENV === "development") {
        console.log("Theme applied to MUI via Redux:", {
          theme: themeData.theme,
          hasColors: !!themeData.colors,
          hasCustomTheme: themeData.theme === "custom",
        });
      }
    },

    onParentReady: () => {
      console.log("Parent window ready for communication");
      // Additional initialization logic can be added here
    },

    trustedOrigins: getTrustedOrigins(),
  });

  // Log initialization status in development
  React.useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      console.log("AppInitializer: Iframe communication status:", {
        isInitialized: iframeComm.status.isInitialized,
        isIframe: iframeComm.status.isIframe,
        parentReady: iframeComm.status.parentReady,
        source: iframeComm.status.source,
        capabilities: iframeComm.status.capabilities,
        theme: iframeComm.status.theme,
        trustedOrigins: getTrustedOrigins(),
      });
    }
  }, [iframeComm.status]);

  return <>{children}</>;
};
